import { BasicError } from "@components/_Misc";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { captureMessage } from "@sentry/react";
import { AuthInfo, isAuthed } from "@services/backend-api";
import socket from "@services/socket";
import { ioEvents } from "@weepy/shared";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

let LAST_CHECK: number | null = null;
/** will only fetch from backend if AUTHINFO_MAXAGE_ms in backend-api.ts is over  */
let CHECK_AGE = 600 * 1000;

interface AuthContextValue
  extends Partial<Pick<AuthInfo, "authed" | "email" | "is_member" | "is_manager" | "picture" | "roles" | "name">> {
  // set: () => void;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);
// AuthContext.displayName = "AuthContext";

export const AuthContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<AuthContextValue>({});
  const [error, setError] = useState("");
  const location = useLocation();

  const check = async (refresh?: boolean) => {
    try {
      const { authed, email, is_member, picture, roles, name, is_manager } = await isAuthed(refresh);
      setState((prev) => ({ ...prev, authed, email, is_member, picture, roles, name, is_manager }));
      LAST_CHECK = Date.now();
    } catch (error) {
      setError((error as Error)?.message);
    }
  };

  useEffect(() => {
    if (!LAST_CHECK) check();
    else if (Date.now() - LAST_CHECK > CHECK_AGE) check();
  }, [location]);

  useEffect(() => {
    const listener = () => check(true);
    socket.on(ioEvents.REFRESH, listener);

    return () => {
      socket.off(ioEvents.REFRESH, listener);
    };
  }, []);

  // useEffect(() => {
  // if a malicious user tries to change the state in react (devtools), validate the values from isAuthed()
  // }, [state.is_member, state.is_manager]);

  if (error && !("authed" in state)) return <BasicError error={error} />;
  if (!("authed" in state)) return <></>; // still loading auth info

  return (
    <GoogleOAuthProvider
      clientId="31061837841-vq4a497h9c3c26dnepe9i0mtuni3qkjl.apps.googleusercontent.com"
      onScriptLoadError={(...args) => {
        captureMessage("GIS_SCRIPT_LOAD_ERROR", { extra: { args } });
      }}
      onScriptLoadSuccess={() => console.log("GIS_SCRIPT_LOAD_SUCCESS")}
    >
      <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
    </GoogleOAuthProvider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
